import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { grantsForScope } from "../rbac/grantQueries";
import { mutator } from "./mutator";

export const deleteConnectorSchema = z.object({
  connectedAppId: z.string(),
});

export const deleteConnectedApp = mutator({
  schema: deleteConnectorSchema,
  resolve: {
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({ tx, args: { app } }) => {
    if (app.oauthConnectionId) {
      const [oauthConn] = await tx.run(
        zql.oauthConnections.where("id", app.oauthConnectionId)
      );
      if (!oauthConn) throw userFacingError("OAuth connection not found");
    }

    if (!app.fresh) {
      const blockers = [
        {
          type: "space_git_remote",
          message:
            "This connector is used for git sync. Disconnect git sync first.",
        },
        {
          type: "audit_log_destination",
          message:
            "This connector is used by an audit log destination. Remove it from the destination first.",
        },
      ] as const;
      const blockingUsages = await tx.run(
        zql.connectedAppUsages.where("connectedAppId", app.id).where(
          "type",
          "IN",
          blockers.map((b) => b.type)
        )
      );
      for (const { type, message } of blockers) {
        if (blockingUsages.some((u) => u.type === type)) {
          throw userFacingError(message);
        }
      }

      const [pendingClaim] = await tx.run(
        zql.connectionClaims
          .where("connectedAppId", app.id)
          .where("status", "pending")
          .limit(1)
      );
      if (pendingClaim) {
        throw userFacingError(
          "This connector has a pending connection request. Resolve or dismiss it first."
        );
      }
    }

    const grants = await tx.run(grantsForScope(app.id, app.tenantId));
    for (const g of grants) {
      await tx.mutate.roleGrants.delete({ id: g.id });
    }

    await tx.mutate.connectedApps.delete({ id: app.id });
    if (app.oauthConnectionId) {
      await tx.mutate.oauthConnections.delete({ id: app.oauthConnectionId });
    }
  },
});
